import React, { useState, useEffect, useImperativeHandle } from 'react';
import { ChevronDown, Globe, MapPin, Building, User, Mail, Phone, Hash, CreditCard } from 'lucide-react';
import { getSession } from '../../getSession';
import { getCountryList, getStateListByCountryId, getCityListByStateId, getLeadCustomerSuggection } from '../../utils/lead';
import Input from '../common/Input';
import Select from '../common/Select';
import Button from '../common/Button';
import Textarea from '../common/Textarea';
import Swal from 'sweetalert2';

const AddTaxSettingForm = React.forwardRef(({ lead, onClose }, ref) => {
    const { userId, TokenId, parentId } = getSession();

    const [formData, setFormData] = useState({
        hasTaxNumber: false,
        taxNumber: '',
        panNumber: '',
        companyName: lead?.CompanyName || '',
        relatedTo: lead?.PersonName || '',
        personName: lead?.PersonName || '',
        mobileNo: lead?.MobileNo || '',
        emailId: lead?.EmailID || '',
        address: lead?.ResidentialAddress || '',
        countryId: '',
        stateId: '',
        cityId: '',
        pincode: lead?.PinCode || '',
        isDefault: true,
        LeadId: lead?.ID || lead?.LeadId || ''
    });

    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const list = await getCountryList({});
                setCountries(Array.isArray(list) ? list : []);
            } catch (err) {
                console.warn('fetchCountries error', err);
            }
        };
        fetchCountries();
    }, []);

    const handleCountryChange = async (e) => {
        const val = e.target.value;
        setFormData(prev => ({ ...prev, countryId: val, stateId: '', cityId: '' }));
        setStates([]);
        setCities([]);
        if (val) {
            try {
                const st = await getStateListByCountryId({ Details: { CountryId: val } });
                setStates(Array.isArray(st) ? st : []);
            } catch (err) {
                console.warn('fetch states error', err);
            }
        }
    };

    const handleStateChange = async (e) => {
        const val = e.target.value;
        setFormData(prev => ({ ...prev, stateId: val, cityId: '' }));
        setCities([]);
        if (val) {
            try {
                const ct = await getCityListByStateId({ Details: { StateId: val } });
                setCities(Array.isArray(ct) ? ct : []);
            } catch (err) {
                console.warn('fetch cities error', err);
            }
        }
    };

    useImperativeHandle(ref, () => ({
        getFormData: () => formData,
        isValid: () => {
            if (!formData.companyName) return { valid: false, message: 'Company Name is required' };
            if (!formData.panNumber) return { valid: false, message: 'PAN Number is required' };
            return { valid: true };
        }
    }));

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleRelatedToChange = async (e) => {
        const val = e.target.value;
        handleChange('relatedTo', val);

        if (val.length > 2) {
            setIsLoadingSuggestions(true);
            try {
                const list = await getLeadCustomerSuggection({
                    Details: {
                        UserId: userId,
                        FilterText: val
                    }
                });
                setSuggestions(list);
                setShowSuggestions(true);
            } catch (err) {
                console.warn('suggestion error', err);
            } finally {
                setIsLoadingSuggestions(false);
            }
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const handleSelectSuggestion = (item) => {
        setFormData(prev => ({
            ...prev,
            relatedTo: item.PersonName || item.EmailID || item.MobileNo || '',
            personName: item.PersonName || '',
            mobileNo: item.MobileNo || '',
            emailId: item.EmailID || '',
            LeadId: item.LeadId || item.LeadID || item.Id || '',
            companyName: item.CompanyName || prev.companyName,
            panNumber: item.PanNumber || prev.panNumber,
            address: item.Address || prev.address,
            taxNumber: item.TaxNumber || prev.taxNumber,
            hasTaxNumber: !!item.TaxNumber || prev.hasTaxNumber,
            countryId: item.CountryId || prev.countryId,
            stateId: item.StateId || prev.stateId,
            cityId: item.CityId || prev.cityId,
            pincode: item.PinCode || prev.pincode
        }));
        setShowSuggestions(false);
    };

    return (
        <div className="bg-white rounded-lg shadow-sm">
            <div className="p-4 bg-gray-50 border-b flex justify-between items-center">
                <h3 className="text-gray-700 font-semibold text-sm uppercase tracking-wider">Lead Tax Settings</h3>
            </div>

            <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Tax Number */}
                    <div className="space-y-2">
                        <label className="block text-xs font-bold text-gray-600 uppercase">Tax Number</label>
                        <div className="flex border rounded-md overflow-hidden bg-gray-50">
                            <div className="flex items-center gap-2 px-3 border-r bg-white min-w-[180px]">
                                <input
                                    type="checkbox"
                                    checked={formData.hasTaxNumber}
                                    onChange={(e) => handleChange('hasTaxNumber', e.target.checked)}
                                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-600">User had tax number?</span>
                            </div>
                            <input
                                type="text"
                                value={formData.taxNumber}
                                onChange={(e) => handleChange('taxNumber', e.target.value)}
                                disabled={!formData.hasTaxNumber}
                                placeholder="Please Enter Tax Number"
                                className="flex-1 px-3 py-2 bg-transparent text-sm focus:outline-none disabled:cursor-not-allowed"
                            />
                        </div>
                    </div>

                    {/* PAN Number */}
                    <div className="space-y-2">
                        <label className="block text-xs font-bold text-gray-600 uppercase">Pan Number</label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <CreditCard className="w-4 h-4 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                value={formData.panNumber}
                                onChange={(e) => handleChange('panNumber', e.target.value)}
                                placeholder="Please Enter Pan Number"
                                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                            />
                        </div>
                    </div>

                    {/* Company Name */}
                    <div className="space-y-2">
                        <label className="block text-xs font-bold text-gray-600 uppercase">Company Name</label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Building className="w-4 h-4 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                value={formData.companyName}
                                onChange={(e) => handleChange('companyName', e.target.value)}
                                placeholder="Please Enter Company Name"
                                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                            />
                        </div>
                    </div>

                    {/* Related To */}
                    <div className="space-y-2">
                        <label className="block text-xs font-bold text-gray-600 uppercase">Related To</label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Hash className="w-4 h-4 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                value={formData.relatedTo}
                                onChange={handleRelatedToChange}
                                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                placeholder="Type Name, email, mobile"
                                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                            />
                            {isLoadingSuggestions && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                </div>
                            )}
                            {showSuggestions && suggestions.length > 0 && (
                                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                                    {suggestions.map((item, idx) => (
                                        <div
                                            key={idx}
                                            onClick={() => handleSelectSuggestion(item)}
                                            className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                                        >
                                            <div className="font-medium text-sm text-gray-900">{item.PersonName}</div>
                                            <div className="text-xs text-gray-500 flex justify-between">
                                                <span>{item.MobileNo}</span>
                                                <span>{item.EmailID}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <input type="hidden" name="LeadId" value={formData.LeadId} />
                    </div>
                </div>

                {/* Display Info */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 py-2 border-t border-b border-dashed border-gray-100">
                    <div className="text-sm">
                        <span className="text-gray-500 font-medium">Lead ID:</span>
                        <span className="ml-2 text-gray-900">{formData.LeadId}</span>
                    </div>
                    <div className="text-sm">
                        <span className="text-gray-500 font-medium">Person Name:</span>
                        <span className="ml-2 text-gray-900">{formData.personName}</span>
                    </div>
                    <div className="text-sm">
                        <span className="text-gray-500 font-medium">Mobile No:</span>
                        <span className="ml-2 text-gray-900">{formData.mobileNo}</span>
                    </div>
                    <div className="text-sm">
                        <span className="text-gray-500 font-medium">Email Id:</span>
                        <span className="ml-2 text-gray-900">{formData.emailId}</span>
                    </div>
                </div>

                {/* Address */}
                <div className="space-y-2">
                    <label className="block text-xs font-bold text-gray-600 uppercase">Address</label>
                    <div className="relative">
                        <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                            <MapPin className="w-4 h-4 text-gray-400" />
                        </div>
                        <textarea
                            value={formData.address}
                            onChange={(e) => handleChange('address', e.target.value)}
                            placeholder="Please Enter Your Address"
                            rows={3}
                            className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Country */}
                    <div className="space-y-2">
                        <label className="block text-xs font-bold text-gray-600 uppercase">Country</label>
                        <select
                            value={formData.countryId}
                            onChange={handleCountryChange}
                            className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-blue-500 outline-none bg-white"
                        >
                            <option value="">-- Select Country --</option>
                            {countries.map(c => (
                                <option key={c.Id || c.id} value={c.Id || c.id}>{c.Text || c.text || c.Name}</option>
                            ))}
                        </select>
                    </div>

                    {/* State */}
                    <div className="space-y-2">
                        <label className="block text-xs font-bold text-gray-600 uppercase">State</label>
                        <select
                            value={formData.stateId}
                            onChange={handleStateChange}
                            className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-blue-500 outline-none bg-white"
                        >
                            <option value="">Nothing selected</option>
                            {states.map(s => (
                                <option key={s.Id || s.id} value={s.Id || s.id}>{s.Text || s.text}</option>
                            ))}
                        </select>
                    </div>

                    {/* City */}
                    <div className="space-y-2">
                        <label className="block text-xs font-bold text-gray-600 uppercase">City</label>
                        <select
                            value={formData.cityId}
                            onChange={(e) => handleChange('cityId', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-blue-500 outline-none bg-white"
                        >
                            <option value="">Nothing selected</option>
                            {cities.map(c => (
                                <option key={c.Id || c.id} value={c.Id || c.id}>{c.Text || c.text}</option>
                            ))}
                        </select>
                    </div>

                    {/* Pincode */}
                    <div className="space-y-2">
                        <label className="block text-xs font-bold text-gray-600 uppercase">Pincode</label>
                        <input
                            type="text"
                            value={formData.pincode}
                            onChange={(e) => handleChange('pincode', e.target.value)}
                            placeholder="Please Enter Pincode Number"
                            className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                        />
                    </div>
                </div>

                {/* Is Default */}
                <div className="space-y-2 pt-2">
                    <label className="block text-xs font-bold text-gray-600 uppercase">is Default</label>
                    <div className="flex items-center gap-4">
                        <div
                            onClick={() => handleChange('isDefault', !formData.isDefault)}
                            className={`relative w-16 h-8 rounded-full cursor-pointer transition-colors duration-200 border ${formData.isDefault ? 'bg-[#00a6e0] border-[#00a6e0]' : 'bg-gray-200 border-gray-300'}`}
                        >
                            <div className={`absolute top-1/2 -translate-y-1/2 px-2 text-[10px] font-bold text-white transition-all ${formData.isDefault ? 'left-1' : 'right-1'}`}>
                                {formData.isDefault ? 'YES' : 'NO'}
                            </div>
                            <div className={`absolute top-1 h-5 w-5 bg-white rounded-full shadow-sm transition-all duration-200 ${formData.isDefault ? 'right-1' : 'left-1'}`}>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default AddTaxSettingForm;
